import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, StatusBadge, Field } from "../components/Shared";
import { ProofReview } from "../components/ProofReview";
import { COACH } from "../constants/testIds";
import { Check, X, Plus, GraduationCap, UserPlus } from "lucide-react";

const TABS = ["IN_QUALIFICATION", "WAITING_REVIEW", "APPROVED", "REJECTED", "ALL"];

export default function CoachCandidates() {
  const { user } = useAuth();
  const { t } = useLang();
  const perms = user?.permissions || [];
  const canReview = perms.includes("coach_candidate.review");
  const canInvite = perms.includes("coach_role.assign") || user?.role === "MASTER_ADMIN";
  const [tab, setTab] = useState("WAITING_REVIEW");
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: "", email: "" });
  const [invitePreview, setInvitePreview] = useState("");
  const [addForm, setAddForm] = useState({ member_email: "", note: "" });
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/coach-candidates?status=${tab}`);
      setItems(data.data.items);
    } catch (e) { toast.error(extractError(e).message); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    try {
      await api.post(`/admin/coach-candidates/${id}/review`, { decision, note: note || null });
      toast.success(decision === "APPROVED" ? t("coach.approvedMsg") : t("coach.rejectedMsg"));
      setNoteFor(null); setNote("");
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const setProgress = async (id, progress) => {
    try {
      await api.patch(`/admin/coach-candidates/${id}/progress`, { progress });
      toast.success(t("coach.progressSaved"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coach-candidates", { member_email: addForm.member_email, note: addForm.note || null });
      toast.success(t("coach.added"));
      setShowAdd(false); setAddForm({ member_email: "", note: "" });
      load();
    } catch (err) { toast.error(extractError(err).message); }
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/coach-invitations", inviteForm);
      toast.success(t("auth.inviteSent"));
      setInvitePreview(data.data.preview_link || "");
      setInviteForm({ full_name: "", email: "" });
    } catch (err) { toast.error(extractError(err).message); }
  };

  return (
    <div data-testid={COACH.page} className="space-y-8">
      <PageHeader eyebrow={t("coach.eyebrow")} title={t("coach.title")} subtitle={t("coach.subtitle")}>
        {canInvite && (
          <Button data-testid="coach-invite-btn" onClick={() => setShowInvite(!showInvite)} className="bg-[#10B981] hover:bg-[#0e9d70] text-white h-11">
            <UserPlus className="h-4 w-4 mr-1" /> {t("auth.inviteCoach")}
          </Button>
        )}
        {canReview && (
          <Button data-testid={COACH.addBtn} onClick={() => setShowAdd(!showAdd)} className="bg-white/10 hover:bg-white/15 text-white h-11">
            <Plus className="h-4 w-4 mr-1" /> {t("coach.addCandidate")}
          </Button>
        )}
      </PageHeader>

      {showInvite && (
        <Card data-testid="coach-invite-form" className="bg-[#10121B] border-[#10B981]/30">
          <CardContent className="p-6 space-y-3">
            <p className="text-xs text-slate-400">{t("auth.inviteHint")}</p>
            <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t("common.fullName")} testId="coach-invite-name-input" value={inviteForm.full_name} onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })} required />
              <Field label={t("common.email")} testId="coach-invite-email-input" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required />
              <div className="sm:col-span-2 flex justify-end">
                <Button data-testid="coach-invite-submit-btn" type="submit" className="bg-[#10B981] hover:bg-[#0e9d70] text-white font-semibold">{t("auth.inviteSend")}</Button>
              </div>
            </form>
            {invitePreview && (
              <p className="text-xs text-slate-400 break-all">
                {t("auth.previewLabel")}:{" "}
                <a data-testid="coach-invite-preview-link" href={invitePreview} className="text-[#10B981] underline">{invitePreview}</a>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {showAdd && (
        <Card data-testid={COACH.addForm} className="bg-[#10121B] border-[#FF3B30]/30">
          <CardContent className="p-6">
            <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t("payments.memberEmail")} testId={COACH.addEmail} type="email" value={addForm.member_email} onChange={(e) => setAddForm({ ...addForm, member_email: e.target.value })} required />
              <Field label={t("coach.note")} testId={COACH.addNote} value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} />
              <div className="sm:col-span-2 flex justify-end">
                <Button data-testid={COACH.addSubmit} type="submit" className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{t("common.save")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-1 border-b border-white/5">
        {TABS.map((s) => (
          <button key={s} data-testid={`coach-tab-${s}`} onClick={() => setTab(s)} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === s ? "border-[#FF3B30] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {t(`coach.tab${s}`)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState testId={COACH.empty} text={t("coach.empty")} />
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <Card key={c.candidate_id} data-testid={`candidate-row-${c.candidate_id}`} className="bg-[#161923] border-white/5">
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <GraduationCap className="h-4 w-4 text-[#10B981]" />
                      <span className="font-semibold text-white">{c.full_name}</span>
                      <span className="text-xs text-slate-500">{c.email}</span>
                      <StatusBadge status={c.status} testId={`candidate-status-${c.candidate_id}`} />
                    </div>
                    {c.motivation && <p className="text-sm text-slate-400 mt-2 leading-relaxed">“{c.motivation}”</p>}
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                      <span>{t("payments.source")}: {c.source}</span>
                      <span>{t("coach.activeEnrollments")}: {c.active_enrollments}</span>
                      <span data-testid={`candidate-progress-${c.candidate_id}`}>{t("coach.progress")}: {c.qualification_progress ?? 0}%</span>
                      {c.whatsapp_number && <span>WA: {c.whatsapp_number}</span>}
                      {c.review_note && <span className="text-slate-300">{t("coach.note")}: {c.review_note}</span>}
                    </div>
                  </div>
                  {c.status === "IN_QUALIFICATION" && canReview && (
                    <div className="flex items-center gap-2">
                      <input data-testid={`candidate-progress-input-${c.candidate_id}`} type="number" min={0} max={100} defaultValue={c.qualification_progress ?? 0} id={`prog-${c.candidate_id}`} className="w-20 h-9 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-2" />
                      <Button data-testid={`candidate-progress-save-${c.candidate_id}`} onClick={() => setProgress(c.candidate_id, Number(document.getElementById(`prog-${c.candidate_id}`).value))} className="bg-white/10 hover:bg-white/15 text-white h-9">{t("coach.saveProgress")}</Button>
                      <Button data-testid={`candidate-complete-${c.candidate_id}`} onClick={() => setProgress(c.candidate_id, 100)} className="bg-sky-600 hover:bg-sky-500 text-white h-9">{t("coach.markComplete")}</Button>
                    </div>
                  )}
                  {c.status === "WAITING_REVIEW" && canReview && noteFor !== c.candidate_id && (
                    <Button data-testid={`candidate-review-${c.candidate_id}`} onClick={() => setNoteFor(c.candidate_id)} className="bg-white/10 hover:bg-white/15 text-white h-9">{t("coach.review")}</Button>
                  )}
                </div>
                <ProofReview candidate={c} canReview={canReview} onDone={load} />
                {noteFor === c.candidate_id && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end pt-2 border-t border-white/5">
                    <div className="flex-1"><Field label={t("coach.note")} testId={COACH.reviewNote} value={note} onChange={(e) => setNote(e.target.value)} /></div>
                    <Button data-testid={`candidate-approve-${c.candidate_id}`} onClick={() => review(c.candidate_id, "APPROVED")} className="bg-emerald-600 hover:bg-emerald-500 text-white h-10"><Check className="h-4 w-4 mr-1" /> {t("coach.approve")}</Button>
                    <Button data-testid={`candidate-reject-${c.candidate_id}`} onClick={() => review(c.candidate_id, "REJECTED")} className="bg-red-600 hover:bg-red-500 text-white h-10"><X className="h-4 w-4 mr-1" /> {t("payments.reject")}</Button>
                    <Button type="button" variant="ghost" onClick={() => setNoteFor(null)} className="text-slate-300 h-10">{t("common.cancel")}</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
